import React, {ElementType, FC, ReactNode, Suspense, useMemo} from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes as RouterRoutes,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
  useMatch,
  To,
  NavigateOptions
} from 'react-router';
import {useDispatch} from "react-redux";
// @ts-ignore
import queryString from 'query-string';
import {initRoutes} from "../store/modules/routes";
import {useAuth} from "./auth";
import {useI18n} from "./i18";
import {debounce} from "lodash-es"

export interface IRoute {
  path: string,
  title: string,
  exact?: boolean,
  inMenu?: boolean,
  icon?: undefined | string | ReactNode | ElementType,
  private?: undefined | boolean,
  permission?: undefined | string,
  inLayout?: boolean,
  component?: any,
  routes?: undefined | IRoute[]
}

/**
 * react-router 7 (RR phase). The hook keeps the v5-era shape consumers use --
 * push / replace / pathname / query / search / replaceQuery / match / location --
 * on top of useNavigate / useLocation / useParams / useMatch. `history` is gone
 * with the history package; push/replace accept the same `To` values as before.
 */
export const useRouter = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // v5's useRouteMatch() (the enclosing route's pattern + params); no consumer reads `match`
  // today, and the route table has no dynamic segments, so the current pathname is its own match.
  const match = useMatch(location.pathname);
  const query = {
    ...queryString.parse(location.search),
    ...params
  };
  const push = (to: To, options?: NavigateOptions) => navigate(to, options);
  const replace = (to: To, options?: NavigateOptions) => navigate(to, {...options, replace: true});
  const search = (value?: {}) => {
    const query_ = {...query, ...value || {}};
    const search_ = Object.keys(query_).reduce((result: any[], key) => {
      if (query_[key]) result.push(`${key}=${query_[key]}`);
      return result;
    }, []).join('&');
    return (search_) ? '?' + search_ : ''
  };
  const replaceQuery_ = (value?: {}) => {
    const search_ = search(value);
    if (search_ !== location.search) {
      replace({pathname: location.pathname, search: search_});
    }
  };
  const replaceQuery = debounce(replaceQuery_, 150);
  return useMemo(() => {
      return {
        push,
        replace,
        pathname: location.pathname,
        query: query,
        search,
        replaceQuery,
        match,
        location
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, match, location, navigate]
  );
};

export const parseRoute = (t: any, route: IRoute, flat: IRoute[], map: any, parent?: IRoute | null) => {
  const permission_ = `${parent?.permission || ''}${route.permission && parent?.permission ? ',' : ''}${route.permission || ''}`;
  let result: IRoute = {
    exact: true,
    inMenu: true,
    private: true,
    inLayout: true,
    ...route,
    permission: permission_,
    path: `${parent ? parent.path : ''}${route.path}`
  };
  result.title = t(result.title);
  if (result.routes) result.routes = result.routes.map(value => parseRoute(t, value, flat, map, result));
  if (result.component) flat.push(result);
  map[result.path] = result;
  return result;
};
export const parser = (config: IRoute[], page404: ElementType, t: any) => {
  let map: {} = {};
  let flat: IRoute[] = [];
  let tree: IRoute[] = [
    ...config,
    {
      path: '*',
      title: '404',
      inMenu: false,
      private: false,
      component: page404
    }
  ].map(value => parseRoute(t, value, flat, map, null));
  return {flat, tree, map};
};

export const LayoutWrapper: FC<any> = (props) => {
  const route = useRouter();
  if (props.layout && props.routes[route.pathname] && props.routes[route.pathname].inLayout) {
    return (<props.layout>{props.children}</props.layout>);
  }
  return (<>{props.children}</>);
};
export const RoutePrivateWrapper: FC = (props: any) => {
  const auth = useAuth();
  if (props.route.private) {
    if (!auth.init && auth.loading) {
      return (<h1>Loading...</h1>);
    } else if (auth.error || !auth.user) {
      // The requested URL travels to the login page as an ABSOLUTE path with its query
      // (G1). v5's history resolved a slash-less `redirect` against the current URL;
      // react-router 7+ resolves it against the /login route, which sent the user to
      // /login/<path> (404) after signing in. The query is kept so a deep link into a
      // patient card / meeting survives the round trip.
      const redirect = encodeURIComponent(`${props.location.pathname}${props.location.search}`);
      return (<Navigate to={{pathname: props.loginPath, search: `?redirect=${redirect}`}} replace/>)
    } else if (auth.permission(props.route.permission)) {
      return (
        <Suspense fallback={<div>Loading...</div>}>
          <props.route.component {...props}/>
        </Suspense>
      )
    } else {
      return <props.page404/>;
    }
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <props.route.component {...props}/>
    </Suspense>
  )
};
/** v5 handed `match` / `location` / `history` to the page through render props; RR 7 has `element`. */
const RouteElement: FC<{route: IRoute, loginPath: string, page404: ElementType}> = (props) => {
  const location = useLocation();
  // @ts-ignore
  return <RoutePrivateWrapper {...props} location={location}/>;
};
export const Routes: FC<{
  routes: IRoute[],
  layout: ElementType,
  page404: ElementType,
  basename?: string,
  loginPath?: string
}> = ({
        routes,
        layout,
        page404,
        basename = '/',
        loginPath = '/login'
      }) => {
  const {t} = useI18n();
  // parse props
  const {flat, tree, map} = parser(routes, page404, t);
  // set store
  const dispatch = useDispatch();
  // TODO: need check init state, now hack to waiting init components
  setTimeout(() => {
    dispatch(initRoutes(tree, flat, map, basename, loginPath));
  }, 100)
  // render
  return (
    // render routes
    <Router basename={basename}>
      {flat.length > 0 &&
      <LayoutWrapper
        layout={layout}
        routes={map}>
        <RouterRoutes>
          {flat.map(route => (
            <Route
              key={route.path}
              path={route.path}
              element={<RouteElement route={route} loginPath={loginPath} page404={page404}/>}
            />
          ))}
        </RouterRoutes>
      </LayoutWrapper>
      }
    </Router>
  )
};

export default useRouter;
